/**
 * A channel and its posts.
 *
 * Note what a Channel does NOT carry: a follower list. Only a count, and the
 * caller's own relationship to it. The privacy promise is kept by the shape
 * of the data rather than by a check somewhere in the UI.
 *
 * @typedef {object} Channel
 * @property {string}      id
 * @property {string}      handle         Without the @, so links can add it
 * @property {string}      name
 * @property {string}      description
 * @property {string|null} avatarUrl
 * @property {boolean}     isVerified
 * @property {number}      followerCount
 * @property {boolean}     isFollowing    The caller's own relationship
 * @property {boolean}     isMuted
 * @property {boolean}     isAdmin        May post
 * @property {boolean}     isOwner        May delete, may add admins
 * @property {number}      unreadCount
 * @property {ChannelPostPreview|null} lastPost
 * @property {string}      createdBy
 * @property {Date}        createdAt
 *
 * @typedef {object} ChannelPostPreview
 * @property {string} id
 * @property {string} type
 * @property {string} preview
 * @property {Date}   createdAt
 *
 * @typedef {object} ChannelPost
 * @property {string}      id
 * @property {string}      channelId
 * @property {string}      authorId
 * @property {'text'|'image'|'video'|'audio'|'document'|'poll'} type
 * @property {string}      body
 * @property {string|null} mediaUrl
 * @property {object|null} mediaMeta
 * @property {number}      viewCount
 * @property {Record<string, number>} reactions  emoji → count, anonymous
 * @property {Date}        createdAt
 * @property {Date|null}   editedAt
 * @property {Date|null}   deletedAt
 */

import { toDate } from '../utils/time.js'

/** Length at which a post stops being a list preview and starts being a wall. */
const PREVIEW_MAX = 120

/**
 * @param {string} id
 * @param {object} raw
 * @returns {Channel}
 */
export function toChannel (id, raw = {}) {
  const last = raw.lastPost ?? raw.last_post ?? null
  return {
    id,
    // Stored bare. A handle that sometimes has an @ and sometimes does not is
    // the kind of difference that only shows up in a broken link.
    handle:        String(raw.handle ?? '').replace(/^@/, ''),
    name:          raw.name ?? '',
    description:   raw.description ?? '',
    avatarUrl:     raw.avatarUrl ?? raw.avatar_url ?? null,
    isVerified:    Boolean(raw.isVerified ?? raw.is_verified),
    followerCount: Number(raw.followerCount ?? raw.follower_count ?? 0),

    isFollowing:   Boolean(raw.isFollowing ?? raw.is_following),
    isMuted:       Boolean(raw.isMuted ?? raw.is_muted),
    isAdmin:       Boolean(raw.isAdmin ?? raw.is_admin),
    isOwner:       Boolean(raw.isOwner ?? raw.is_owner),
    unreadCount:   Number(raw.unreadCount ?? raw.unread_count ?? 0),

    lastPost: last ? {
      id:        last.id,
      type:      last.type ?? 'text',
      preview:   last.preview ?? '',
      createdAt: toDate(last.createdAt ?? last.created_at) ?? new Date()
    } : null,

    createdBy: raw.createdBy ?? raw.created_by ?? '',
    createdAt: toDate(raw.createdAt ?? raw.created_at) ?? new Date()
  }
}

/**
 * @param {string} id
 * @param {object} raw
 * @returns {ChannelPost}
 */
export function toChannelPost (id, raw = {}) {
  return {
    id,
    channelId: raw.channelId ?? raw.channel_id ?? '',
    authorId:  raw.authorId ?? raw.author_id ?? '',
    type:      raw.type ?? 'text',
    body:      raw.body ?? '',
    mediaUrl:  raw.mediaUrl ?? raw.media_url ?? null,
    mediaMeta: raw.mediaMeta ?? raw.media_meta ?? null,
    viewCount: Number(raw.viewCount ?? raw.view_count ?? 0),
    // Always an object, never undefined: every render site counts entries.
    reactions: raw.reactions ?? {},
    createdAt: toDate(raw.createdAt ?? raw.created_at) ?? new Date(),
    editedAt:  toDate(raw.editedAt ?? raw.edited_at),
    deletedAt: toDate(raw.deletedAt ?? raw.deleted_at)
  }
}

/** One line for a channel row in the Updates list. */
export function postPreview (post) {
  if (!post) return ''
  if (post.deletedAt) return 'This post was deleted'

  const label = {
    image:    'Photo',
    video:    'Video',
    audio:    'Audio',
    document: 'Document',
    poll:     'Poll'
  }[post.type]

  const body = (post.body ?? '').trim()
  if (label) return body ? `${label}: ${body}` : label
  return body.length > PREVIEW_MAX ? `${body.slice(0, PREVIEW_MAX)}…` : body
}

/**
 * "12.4K followers" — the form every channel header uses.
 *
 * A raw 50000 reads as a database field; the rounded form reads as a number
 * of people, which is what the follower count is for.
 */
export function followerLabel (count = 0) {
  const n = Number(count) || 0
  if (n === 1) return '1 follower'
  if (n < 1000) return `${n} followers`
  // One decimal, dropped when it is zero: 12.4K, but 54K rather than 54.0K.
  // Rounding to a whole number instead turned 12,400 into "12K", which loses
  // four hundred people to make the string one character shorter.
  const short = value => value.toFixed(1).replace(/\.0$/, '')
  if (n < 1_000_000) return `${short(n / 1000)}K followers`
  return `${short(n / 1_000_000)}M followers`
}

/**
 * Handles are the public identifier, so the rules are strict and enforced in
 * one place: lowercase, 5–30 characters, letters, digits and underscores,
 * starting with a letter.
 *
 * @returns {{ ok: true, handle: string } | { ok: false, reason: string }}
 */
export function normaliseHandle (input) {
  const handle = String(input ?? '').trim().replace(/^@/, '').toLowerCase()
  if (handle.length < 5) return { ok: false, reason: 'A handle needs at least 5 characters.' }
  if (handle.length > 30) return { ok: false, reason: 'A handle can be at most 30 characters.' }
  if (!/^[a-z][a-z0-9_]*$/.test(handle)) {
    return { ok: false, reason: 'Use letters, numbers and underscores, starting with a letter.' }
  }
  return { ok: true, handle }
}
